// src/pages/calculadora-iopos/index2.tsx
import {
    Calculator,
    CheckCircle2,
    MinusCircle,
    TrendingUp,
    XCircle,
} from "lucide-react";
import React, { useState } from "react";
import Layout from "../../components/layouts/Layout";
import { cn } from "../../utils/cn";

const Component = () => {
    const [maxGrade, setMaxGrade] = useState("10");
    const [penaltyIncorrect, setPenaltyIncorrect] = useState("0");
    const [correctAnswers, setCorrectAnswers] = useState("0");
    const [incorrectAnswers, setIncorrectAnswers] = useState("0");
    const [blankAnswers, setBlankAnswers] = useState("0");
    const [result, setResult] = useState<number | null>(null);

    const scaleGrade =
        result !== null ? (result * 10) / Number.parseFloat(maxGrade) : 0;

    const calculateGrade = () => {
        const max = Number.parseFloat(maxGrade) || 10;
        const penaltyEvery = Number.parseFloat(penaltyIncorrect) || 0; // ej: 4 => cada 4 incorrectas restan 1 correcta
        const correct = Number.parseFloat(correctAnswers) || 0;
        const incorrect = Number.parseFloat(incorrectAnswers) || 0;
        const blank = Number.parseFloat(blankAnswers) || 0;

        const totalQuestions = correct + incorrect + blank;
        if (totalQuestions === 0) {
            setResult(0);
            return;
        }

        const penalizedCorrect =
            penaltyEvery > 0 ? incorrect / penaltyEvery : 0;
        const netCorrect = correct - penalizedCorrect;

        const grade = (netCorrect / totalQuestions) * max;
        setResult(Math.max(0, grade));
    };

    return (
        <Layout
            seo={{
                title: "Calculadora de notas iOpos",
                description:
                    "Calcula tu puntuación en exámenes tipo test (sobre 10 u otra nota máxima) según aciertos, fallos y blancos.",
            }}
        >
            <div className="container py-10 lg:py-20">
                {/** Header */}
                <div className="space-y-4 text-center">
                    <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-sky-700/20 bg-sky-700/15 px-5 py-2.5 text-sky-700 shadow-sm">
                        <Calculator className="h-4 w-4" />
                        <span className="text-sm font-semibold">
                            iOpos Academia
                        </span>
                    </div>
                    <h1 className="mb-6 text-balance bg-gradient-to-br from-cpurple via-cpurple to-cpurple bg-clip-text text-5xl font-bold leading-tight text-transparent sm:text-6xl">
                        Calculadora de Notas
                    </h1>

                    <p className="mx-auto max-w-2xl text-balance text-lg font-medium leading-relaxed text-gray-500">
                        En iOpos Academia, puedes seleccionar cualquiera de los
                        métodos oficiales de evaluación de las oposiciones o
                        personalizar los valores para calcular la nota de
                        cualquier otro examen tipo test.
                    </p>

                    <p className="mx-auto max-w-xl pt-2 text-sm text-gray-700/80">
                        Esta herramienta te mostrará tu puntuación en relación a
                        la puntuación total del examen, ya sea sobre 10 u otra
                        puntuación total.
                    </p>
                </div>

                <div className="mt-20 rounded-2xl border-2 border-gray-500/10 bg-white/95 p-8 shadow-2xl backdrop-blur-sm sm:p-10">
                    <div className="grid gap-8">
                        {/* Configuration Section */}
                        <div className="border-border/50 grid gap-8 border-b-2 pb-8 sm:grid-cols-2">
                            <div className="space-y-3">
                                <label
                                    htmlFor="maxGrade"
                                    className="flex items-center gap-2.5 text-base font-semibold"
                                >
                                    <div className="rounded-full bg-gray-500/10 p-1.5">
                                        <TrendingUp className="h-4 w-4 text-sky-700" />
                                    </div>
                                    Nota Máxima
                                </label>
                                <input
                                    id="maxGrade"
                                    type="number"
                                    value={maxGrade}
                                    onChange={(e) =>
                                        setMaxGrade(e.target.value)
                                    }
                                    className="h-14 w-full rounded-md border-2 border-gray-200 px-5 text-lg font-semibold transition-all focus-visible:ring-2 focus-visible:ring-sky-500/20"
                                    placeholder="10"
                                />
                                <p className="text-sm font-medium leading-relaxed text-gray-500">
                                    Nota máxima que se puede conseguir en el
                                    examen
                                </p>
                            </div>

                            <div className="space-y-3">
                                <label
                                    htmlFor="penalty"
                                    className="flex items-center gap-2.5 text-base font-semibold"
                                >
                                    <div className="rounded-full bg-red-500/10 p-1.5">
                                        <XCircle className="h-4 w-4 text-red-500" />
                                    </div>
                                    Respuestas Incorrectas Restan
                                </label>
                                <input
                                    id="penalty"
                                    type="number"
                                    value={penaltyIncorrect}
                                    onChange={(e) =>
                                        setPenaltyIncorrect(e.target.value)
                                    }
                                    className="h-14 w-full rounded-md border-2 border-gray-200 px-5 text-lg font-semibold transition-all focus-visible:border-red-500 focus-visible:ring-2 focus-visible:ring-red-500/20"
                                    placeholder="0"
                                    // step="0.1"
                                />
                                <p className="text-sm font-medium leading-relaxed text-gray-500">
                                    En el caso que las respuestas incorrectan
                                    resten has de introducir cada cuantas
                                    respuestas incorrectas invalidan una de
                                    correcta
                                </p>
                            </div>
                        </div>

                        {/* Answers Section */}
                        <div className="grid gap-6 sm:grid-cols-3">
                            <div className="group space-y-3">
                                <label
                                    htmlFor="correct"
                                    className="flex items-center gap-2.5 text-base font-semibold"
                                >
                                    <div className="rounded-full bg-green-500/10 p-1.5 transition-colors group-hover:bg-green-500/20">
                                        <CheckCircle2 className="h-5 w-5 text-green-600" />
                                    </div>
                                    Respuestas Correctas
                                </label>
                                <input
                                    id="correct"
                                    type="number"
                                    value={correctAnswers}
                                    onChange={(e) =>
                                        setCorrectAnswers(e.target.value)
                                    }
                                    className="h-16 w-full rounded-xl border-2 px-5 text-center text-xl font-bold transition-all focus-visible:border-green-500 focus-visible:ring-2 focus-visible:ring-green-500/20"
                                    placeholder="0"
                                />
                            </div>

                            <div className="group space-y-3">
                                <label
                                    htmlFor="incorrect"
                                    className="flex items-center gap-2.5 text-base font-semibold"
                                >
                                    <div className="rounded-full bg-red-500/10 p-1.5 transition-colors group-hover:bg-red-500/20">
                                        <XCircle className="h-5 w-5 text-red-600" />
                                    </div>
                                    Respuestas Incorrectas
                                </label>
                                <input
                                    id="incorrect"
                                    type="number"
                                    value={incorrectAnswers}
                                    onChange={(e) =>
                                        setIncorrectAnswers(e.target.value)
                                    }
                                    className="h-16 w-full rounded-xl border-2 px-5 text-center text-xl font-bold transition-all focus-visible:border-red-500 focus-visible:ring-2 focus-visible:ring-red-500/20"
                                    placeholder="0"
                                />
                            </div>

                            <div className="group space-y-3">
                                <label
                                    htmlFor="blank"
                                    className="flex items-center gap-2.5 text-base font-semibold"
                                >
                                    <div className="rounded-full bg-gray-200 p-1.5 transition-colors group-hover:bg-gray-300">
                                        <MinusCircle className="h-5 w-5 text-gray-500" />
                                    </div>
                                    Respuestas en Blanco
                                </label>
                                <input
                                    id="blank"
                                    type="number"
                                    value={blankAnswers}
                                    onChange={(e) =>
                                        setBlankAnswers(e.target.value)
                                    }
                                    className="h-16 w-full rounded-xl border-2 px-5 text-center text-xl font-bold transition-all focus-visible:ring-2"
                                    placeholder="0"
                                />
                            </div>
                        </div>

                        {/* Calculate Button */}
                        <button
                            onClick={calculateGrade}
                            // size="lg"
                            className="flex h-16 w-full items-center justify-center rounded-xl bg-glacier-700 text-lg font-bold text-white shadow-xl transition-all duration-200 hover:scale-[1.02] hover:bg-glacier-800 hover:shadow-2xl"
                        >
                            <Calculator className="mr-2 h-6 w-6" />
                            Calcular Nota
                        </button>

                        {/* Result Display */}
                        {result !== null && (
                            <div
                                className={cn(
                                    "animate-in fade-in slide-in-from-bottom-4 relative overflow-hidden rounded-2xl bg-gradient-to-br p-10 shadow-xl duration-500",
                                    scaleGrade >= 8 &&
                                        "border-2 border-sky-600/30 from-sky-600/10 via-sky-600/5 to-sky-600/10",
                                    scaleGrade >= 5 &&
                                        result < 8 &&
                                        "border-2 border-yellow-600/30 from-yellow-600/10 via-yellow-600/5 to-yellow-600/10",
                                    scaleGrade < 5 &&
                                        "border-2 border-red-600/30 from-red-600/10 via-red-600/5 to-red-600/10",
                                )}
                            >
                                <div className="bg-grid-white/5 absolute inset-0 [mask-image:radial-gradient(white,transparent_85%)]" />
                                <div className="relative space-y-4 text-center">
                                    <p
                                        className={cn(
                                            "text-sm font-bold uppercase tracking-widest",
                                            scaleGrade >= 8 && "text-sky-600",
                                            scaleGrade >= 5 &&
                                                "text-yellow-600",
                                            scaleGrade < 5 && "text-red-600",
                                        )}
                                    >
                                        Tu Nota Final
                                    </p>

                                    <p
                                        className={cn(
                                            "text-8xl font-black tracking-tight sm:text-9xl",
                                            scaleGrade >= 8 && "text-sky-600",
                                            scaleGrade >= 5 &&
                                                scaleGrade < 8 &&
                                                "text-yellow-600",
                                            scaleGrade < 5 && "text-red-600",
                                        )}
                                    >
                                        {result.toFixed(2)}
                                    </p>

                                    <p className="text-lg font-medium text-gray-500">
                                        sobre {maxGrade} puntos
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default Component;
